/**
 * Professional Report Form Page
 *
 * Create and edit radiology reports with professional workflow:
 * - Split-view layout with study info and dictation panel
 * - Clear semantic sections for medical reports
 * - Prominent voice dictation with visual feedback
 * - Dictation only activates when focused on a textarea
 *
 * @module presentation/pages/reports
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
  FileText,
  Stethoscope,
  ClipboardList,
  MessageSquare,
  Lightbulb,
  Eye,
  ExternalLink,
  Check,
  ChevronRight,
  Mic,
} from 'lucide-react'

import {
  useReport,
  useCreateReport,
  useUpdateReport,
  canEditReport,
} from '@/application/reports'
import { useStudy } from '@/application/studies'
import { usePatient } from '@/application/patients'
import { toast } from '@/lib/toast'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import { DictationPanel } from '@/presentation/components/DictationPanel'
import { useDictationAvailable } from '@/application/dictation'

// =============================================================================
// Types
// =============================================================================

type ReportSection = 'technique' | 'findings' | 'impression' | 'recommendations'

interface SectionConfig {
  key: ReportSection
  label: string
  icon: React.ElementType
  placeholder: string
  required: boolean
  rows: number
  description: string
}

// =============================================================================
// Constants
// =============================================================================

const SECTIONS: SectionConfig[] = [
  {
    key: 'technique',
    label: 'Técnica',
    icon: Stethoscope,
    placeholder: 'Describa la técnica utilizada para el estudio...',
    required: false,
    rows: 3,
    description: 'Protocolo y parámetros técnicos del estudio',
  },
  {
    key: 'findings',
    label: 'Hallazgos',
    icon: ClipboardList,
    placeholder: 'Describa los hallazgos observados en el estudio de manera sistemática...',
    required: true,
    rows: 8,
    description: 'Descripción sistemática de los hallazgos radiológicos',
  },
  {
    key: 'impression',
    label: 'Impresión Diagnóstica',
    icon: MessageSquare,
    placeholder: 'Conclusión diagnóstica basada en los hallazgos...',
    required: true,
    rows: 4,
    description: 'Interpretación y diagnóstico principal',
  },
  {
    key: 'recommendations',
    label: 'Recomendaciones',
    icon: Lightbulb,
    placeholder: 'Recomendaciones de seguimiento o estudios adicionales (opcional)...',
    required: false,
    rows: 3,
    description: 'Sugerencias de seguimiento o estudios complementarios',
  },
]

// =============================================================================
// Sub-components
// =============================================================================

function WorkflowIndicator({ currentStatus }: { currentStatus: string }) {
  const steps = [
    { key: 'DRAFT', label: 'Borrador', description: 'En edición' },
    { key: 'PRELIMINARY', label: 'Por Firmar', description: 'Listo para firma' },
    { key: 'FINAL', label: 'Firmado', description: 'Finalizado' },
  ]

  const currentIndex = steps.findIndex((s) => s.key === currentStatus)

  return (
    <div className="flex items-center gap-1">
      {steps.map((step, index) => {
        const isCompleted = index < currentIndex
        const isCurrent = index === currentIndex
        const isPending = index > currentIndex

        return (
          <div key={step.key} className="flex items-center">
            <div
              className={`
                flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium
                ${isCompleted ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' : ''}
                ${isCurrent ? 'bg-[var(--primary)] text-white' : ''}
                ${isPending ? 'bg-[var(--background-subtle)] text-[var(--foreground-muted)]' : ''}
              `}
            >
              {isCompleted && <Check className="h-3 w-3" />}
              <span>{step.label}</span>
            </div>
            {index < steps.length - 1 && (
              <ChevronRight className="h-4 w-4 text-[var(--foreground-muted)] mx-0.5" />
            )}
          </div>
        )
      })}
    </div>
  )
}

function StudyInfoCard({
  study,
  patient,
}: {
  study: { id: string; studyDescription: string | null; modality: string; studyDate: string }
  patient: { firstName: string; lastName: string; document: { number: string } } | undefined
}) {
  return (
    <Card className="bg-gradient-to-br from-[var(--primary)]/5 to-[var(--primary)]/10 border-[var(--primary)]/20">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[var(--primary)]/10 rounded-lg">
              <FileText className="h-5 w-5 text-[var(--primary)]" />
            </div>
            <div>
              <p className="font-medium text-[var(--foreground)]">
                {study.studyDescription || 'Estudio sin descripción'}
              </p>
              <p className="text-sm text-[var(--foreground-muted)] mt-0.5">
                <span className="font-medium">{study.modality}</span>
                {' • '}
                {new Date(study.studyDate).toLocaleDateString('es-CO', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
              {patient && (
                <p className="text-sm text-[var(--foreground-muted)] mt-1">
                  Paciente: {patient.firstName} {patient.lastName}
                  {' • '}
                  <span className="font-mono text-xs">{patient.document.number}</span>
                </p>
              )}
            </div>
          </div>
          <Link
            to={`/viewer/${study.id}`}
            target="_blank"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--primary)] hover:bg-[var(--primary)]/10 rounded-md transition-colors"
          >
            <Eye className="h-4 w-4" />
            Ver imágenes
            <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

function SectionHeader({
  section,
  isActive,
  hasContent,
}: {
  section: SectionConfig
  isActive: boolean
  hasContent: boolean
}) {
  const Icon = section.icon

  return (
    <div
      className={`
        flex items-center justify-between mb-2 transition-colors
        ${isActive ? 'text-[var(--primary)]' : 'text-[var(--foreground)]'}
      `}
    >
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${isActive ? 'text-[var(--primary)]' : 'text-[var(--foreground-muted)]'}`} />
        <label className="text-sm font-medium">
          {section.label}
          {section.required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {hasContent && !isActive && (
          <Check className="h-3.5 w-3.5 text-green-500" />
        )}
      </div>
      <span className="text-xs text-[var(--foreground-muted)]">
        {section.description}
      </span>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function ReportFormPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const isEditing = !!id
  const studyIdParam = searchParams.get('studyId')
  const isVoiceSupported = useDictationAvailable()

  // Form state
  const [formData, setFormData] = useState({
    studyId: studyIdParam || '',
    title: '',
    technique: '',
    findings: '',
    impression: '',
    recommendations: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [activeSection, setActiveSection] = useState<ReportSection | null>(null)
  /**
   * Panel de dictado abierto o cerrado.
   *
   * No puede ser `useState(isVoiceSupported)`: el valor inicial de useState
   * se toma en el primer render y no se vuelve a mirar, y ahora saber si hay
   * dictado exige preguntarle al servidor. En el primer render la respuesta
   * todavia no ha llegado, asi que el panel quedaria cerrado para siempre
   * aunque el dictado funcionara.
   *
   * `null` significa que el usuario no ha tocado el boton y manda lo que diga
   * la disponibilidad; en cuanto lo toca, manda su decision.
   */
  const [dictationPanelChoice, setDictationPanelChoice] = useState<boolean | null>(null)
  const showDictationPanel = dictationPanelChoice ?? isVoiceSupported

  // Refs for textareas and dictation state
  const textareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({})
  // Keep track of last active section for dictation (survives blur)
  const lastActiveSectionRef = useRef<ReportSection | null>(null)

  // Queries
  const { data: existingReport, isLoading: reportLoading } = useReport(id)
  const { data: study, isLoading: studyLoading } = useStudy(formData.studyId || undefined)
  const { data: patient } = usePatient(study?.patientId)

  // Mutations
  const createMutation = useCreateReport()
  const updateMutation = useUpdateReport()

  // Populate form when editing
  useEffect(() => {
    if (existingReport && isEditing) {
      setFormData({
        studyId: existingReport.studyId,
        title: existingReport.title,
        technique: '',
        findings: existingReport.findings,
        impression: existingReport.impression,
        recommendations: existingReport.recommendations || '',
      })
    }
  }, [existingReport, isEditing])

  // Auto-generate title from study description
  useEffect(() => {
    if (!formData.title && study?.studyDescription && !isEditing) {
      setFormData((prev) => ({
        ...prev,
        title: `Informe de ${study.studyDescription}`,
      }))
    }
  }, [study, formData.title, isEditing])

  // Check if editing is allowed
  useEffect(() => {
    if (existingReport && isEditing && !canEditReport(existingReport.status)) {
      navigate(`/reports/${id}`, { replace: true })
    }
  }, [existingReport, isEditing, id, navigate])

  const handleChange = useCallback((field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }, [errors])

  const handleDictation = useCallback((text: string, seccionDelDictado?: string) => {
    /**
     * El destino lo dice el panel: es la seccion en la que se pulso dictar,
     * no la que tenga el foco ahora.
     *
     * Al detener llega todavia el ultimo trozo de audio grabado, y para
     * entonces el radiologo suele haber pasado al campo siguiente. Con el
     * foco como destino, una frase de Hallazgos terminaba dentro de la
     * Impresion Diagnostica, que es la parte del informe con valor legal.
     *
     * El foco queda de reserva para el caso en que el panel no lo informe.
     */
    const targetSection = (seccionDelDictado as ReportSection | undefined)
      || activeSection
      || lastActiveSectionRef.current
    if (!targetSection) return

    setFormData((prev) => {
      const currentValue = prev[targetSection] || ''
      const separator = currentValue && !currentValue.endsWith(' ') && !currentValue.endsWith('\n') ? ' ' : ''
      return {
        ...prev,
        [targetSection]: currentValue + separator + text,
      }
    })

    // Focus the corresponding textarea and scroll to bottom
    const textarea = textareaRefs.current[targetSection]
    if (textarea) {
      textarea.focus()
      textarea.scrollTop = textarea.scrollHeight
    }
  }, [activeSection])

  const handleSectionFocus = useCallback((section: ReportSection) => {
    setActiveSection(section)
    lastActiveSectionRef.current = section
  }, [])

  const handleSectionBlur = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
    // Check if focus is moving to dictation panel
    const relatedTarget = e.relatedTarget as HTMLElement | null
    const isMovingToDictationPanel = relatedTarget?.closest('[data-dictation-panel]')
    const isMovingToTextarea = relatedTarget?.tagName === 'TEXTAREA'

    // Don't reset if moving to dictation panel or another textarea
    if (isMovingToDictationPanel || isMovingToTextarea) {
      return
    }

    // Check after a delay for edge cases
    setTimeout(() => {
      const activeElement = document.activeElement
      const isInDictationPanel = activeElement?.closest('[data-dictation-panel]')
      const isTextarea = activeElement?.tagName === 'TEXTAREA'

      // Only reset if not in dictation panel and not in a textarea
      if (!isInDictationPanel && !isTextarea) {
        setActiveSection(null)
      }
    }, 200)
  }, [])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.studyId) {
      newErrors.studyId = 'El estudio es requerido'
    }
    if (!formData.title.trim()) {
      newErrors.title = 'El título es requerido'
    }
    if (!formData.findings.trim()) {
      newErrors.findings = 'Los hallazgos son requeridos'
    }
    if (!formData.impression.trim()) {
      newErrors.impression = 'La impresión diagnóstica es requerida'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) return

    try {
      if (isEditing && id) {
        await updateMutation.mutateAsync({
          id,
          data: {
            title: formData.title,
            findings: formData.findings,
            impression: formData.impression,
            recommendations: formData.recommendations || undefined,
          },
        })
        toast.success('Informe actualizado')
        navigate(`/reports/${id}`)
      } else {
        const newReport = await createMutation.mutateAsync({
          studyId: formData.studyId,
          title: formData.title,
          findings: formData.findings,
          impression: formData.impression,
          recommendations: formData.recommendations || undefined,
        })
        toast.success('Informe creado')
        navigate(`/reports/${newReport.id}`)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al guardar el informe')
    }
  }

  const isLoading = reportLoading || studyLoading
  const isSaving = createMutation.isPending || updateMutation.isPending
  const currentStatus = existingReport?.status || 'DRAFT'

  // Redirect if no study selected (only for new reports)
  useEffect(() => {
    if (!isEditing && !studyIdParam) {
      navigate('/studies', { replace: true })
    }
  }, [isEditing, studyIdParam, navigate])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)] mx-auto mb-4" />
          <p className="text-[var(--foreground-muted)]">Cargando...</p>
        </div>
      </div>
    )
  }

  // Don't render form if no study (will redirect)
  if (!isEditing && !studyIdParam) {
    return null
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-[var(--foreground)]">
              {isEditing ? 'Editar Reporte' : 'Nuevo Informe Radiológico'}
            </h1>
            <p className="text-sm text-[var(--foreground-muted)]">
              {isEditing
                ? 'Modificar informe radiológico existente'
                : 'Crear nuevo informe radiológico'}
            </p>
          </div>
        </div>
        <WorkflowIndicator currentStatus={currentStatus} />
      </div>

      {/* Main Content - Split View */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Left Panel - Form */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Study Info */}
          {study && (
            <div className="mb-4 flex-shrink-0">
              <StudyInfoCard study={study} patient={patient} />
            </div>
          )}

          {/* Error message */}
          {(createMutation.isError || updateMutation.isError) && (
            <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 mb-4 flex-shrink-0">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 text-red-700 dark:text-red-300">
                  <AlertCircle className="h-5 w-5" />
                  <span>
                    {(createMutation.error || updateMutation.error)?.message ||
                      'Error al guardar el reporte'}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
            <Card className="flex-1 flex flex-col min-h-0">
              <CardContent className="p-4 flex-1 flex flex-col min-h-0 overflow-auto">
                {/* Title */}
                <div className="mb-4 flex-shrink-0">
                  <label className="text-sm font-medium text-[var(--foreground)] mb-2 block">
                    Título del Informe *
                  </label>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Ej: Informe de Tomografía de Tórax"
                    className={errors.title ? 'border-red-500' : ''}
                  />
                  {errors.title && (
                    <p className="text-sm text-red-500 mt-1">{errors.title}</p>
                  )}
                </div>

                {/* Semantic Sections */}
                <div className="flex-1 space-y-4 overflow-auto">
                  {SECTIONS.map((section) => (
                    <div
                      key={section.key}
                      className={`
                        p-3 rounded-lg border transition-all
                        ${activeSection === section.key
                          ? 'border-[var(--primary)] bg-[var(--primary)]/5 ring-1 ring-[var(--primary)]/20'
                          : 'border-[var(--border)] hover:border-[var(--primary)]/30'}
                      `}
                    >
                      <SectionHeader
                        section={section}
                        isActive={activeSection === section.key}
                        hasContent={!!formData[section.key]?.trim()}
                      />
                      <textarea
                        ref={(el) => { textareaRefs.current[section.key] = el }}
                        value={formData[section.key]}
                        onChange={(e) => handleChange(section.key, e.target.value)}
                        onFocus={() => handleSectionFocus(section.key)}
                        onBlur={handleSectionBlur}
                        placeholder={section.placeholder}
                        rows={section.rows}
                        className={`
                          w-full rounded-md border bg-[var(--background)] px-3 py-2 text-sm
                          focus:outline-none focus:ring-2 focus:ring-[var(--ring)] resize-none
                          ${errors[section.key] ? 'border-red-500' : 'border-[var(--border)]'}
                        `}
                      />
                      {errors[section.key] && (
                        <p className="text-sm text-red-500 mt-1">{errors[section.key]}</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-[var(--border)] mt-4 flex-shrink-0">
                  <div className="flex items-center gap-2 text-sm text-[var(--foreground-muted)]">
                    {isVoiceSupported && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setDictationPanelChoice(!showDictationPanel)}
                        className={showDictationPanel ? 'text-[var(--primary)]' : ''}
                      >
                        <Mic className="h-4 w-4 mr-1.5" />
                        {showDictationPanel ? 'Ocultar dictado' : 'Mostrar dictado'}
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      disabled={isSaving}
                    >
                      Cancelar
                    </Button>
                    <Button type="submit" disabled={isSaving}>
                      {isSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          {isEditing ? 'Guardar Cambios' : 'Crear Borrador'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </form>
        </div>

        {/* Right Panel - Dictation */}
        {showDictationPanel && isVoiceSupported && (
          <div className="w-80 flex-shrink-0">
            <DictationPanel
              onTranscript={handleDictation}
              activeSection={activeSection}
              disabled={isSaving || !activeSection}
              className="sticky top-0"
            />
          </div>
        )}
      </div>
    </div>
  )
}
